import { useMemo } from 'react'

import { useInfiniteQuery } from '@tanstack/react-query'

import {
  listInsertionOrdersKey,
  listInsertionOrdersService
} from '~/modules/retailMedia/services/insertion-orders'
import { flattenInsertionOrderPages } from '~/modules/retailMedia/services/insertion-orders/flattenPages'
import { getInsertionOrdersNextPageParam } from '~/modules/retailMedia/services/insertion-orders/getNextPageParam'

type UseInsertionOrdersInfiniteQueryFilters = {
  query?: string
  advertiser_id?: string
  status?: InsertionOrderStatus
  start_date?: string
  end_date?: string
  quantity?: number
}

type UseInsertionOrdersInfiniteQueryOptions = {
  enabled?: boolean
}

const buildListParams = (
  filters: UseInsertionOrdersInfiniteQueryFilters,
  page?: number
): ListInsertionOrdersParams => {
  const params: ListInsertionOrdersParams = {}

  if (filters?.query !== undefined) params.query = filters.query
  if (filters?.advertiser_id !== undefined) {
    params.advertiser_id = filters?.advertiser_id
  }
  if (filters?.status !== undefined) params.status = filters?.status
  if (filters?.start_date !== undefined) params.start_date = filters?.start_date
  if (filters?.end_date !== undefined) params.end_date = filters?.end_date
  if (filters?.quantity !== undefined) params.quantity = filters?.quantity
  if (page !== undefined) params.page = page

  return params
}

export const useInsertionOrdersInfiniteQuery = (
  filters: UseInsertionOrdersInfiniteQueryFilters = {},
  options: UseInsertionOrdersInfiniteQueryOptions = {}
) => {
  const listFilters = buildListParams(filters)
  const enabled = options.enabled ?? true

  const query = useInfiniteQuery({
    queryKey: [listInsertionOrdersKey, listFilters],
    queryFn: async ({ pageParam }) => {
      const response = await listInsertionOrdersService(
        buildListParams(filters, pageParam)
      )

      return response.data
    },
    initialPageParam: 1,
    getNextPageParam: getInsertionOrdersNextPageParam,
    enabled
  })

  const items = useMemo(
    () => flattenInsertionOrderPages(query?.data),
    [query?.data]
  )

  return {
    ...query,
    items,
    isLoading: query?.isPending,
    isError: query?.isError,
    error: query?.error,
    fetchNextPage: query?.fetchNextPage,
    hasNextPage: Boolean(query?.hasNextPage),
    isFetchingNextPage: query?.isFetchingNextPage
  }
}
